import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as HamburguerMenu } from "@assets/Icons/IconHamburguerMenu.svg";
import { ReactComponent as LeftIcon } from "@assets/Icons/IconLeft.svg";
import { useAppDispatch } from "../../../shared/redux/hooks";
import { getBackRoute, getPageTitle } from "./helpers/getPageTitle";
import RenderTabsByRoute from "./helpers/getRenderTabsByRoute";
import { usePageHeaderActionsSlot } from "../PageHeaderActions";
import { useWindowSize } from "@shared/hooks/useWindowSize";
import { toggleMobileMenu } from "@redux/slices/sideMenuSlice";
import GenericButton from "@ui-components/Button/GenericButton";
import { SideNavTitleSlotTarget, routeUsesTitleSlot } from "./SideNavTitleSlot";

/**
 * Top bar: page title + Back button + route tabs/filters, plus the mobile
 * hamburger. The former chrome (partner/customer selectors and the profile
 * menu) now lives in the sidebar account card (SideMenuAccountCard).
 */
export default function SideNav() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitle = getPageTitle(location.pathname);
  const backRoute = getBackRoute(location.pathname, location.search);
  const headerActions = usePageHeaderActionsSlot();
  const usesTitleSlot = routeUsesTitleSlot(location.pathname);

  // Mobile detection — only update when crossing the breakpoint.
  const { width } = useWindowSize();
  const [isMobile, setIsMobile] = useState(() => width <= 1024);
  const prevIsMobileRef = useRef(isMobile);
  useEffect(() => {
    const newIsMobile = width <= 1024;
    if (prevIsMobileRef.current !== newIsMobile) {
      prevIsMobileRef.current = newIsMobile;
      setIsMobile(newIsMobile);
    }
  }, [width]);

  const handleHamburgerClick = () => {
    dispatch(toggleMobileMenu());
  };

  return (
    <header
      className="
        w-full
        bg-semantic-bg-primary
        text-semantic-text-secondary
        shadow-md
        px-6
        py-3
        flex
        flex-col
        gap-2
      "
    >
      {/* Mobile-only hamburger row. Desktop chrome (partner/customer, profile)
          moved into the sidebar account card, so there's no top-bar row here. */}
      {isMobile && (
        <div
          className="
            flex items-center
            pb-2
            -mx-6 px-6
            border-b border-semantic-border-secondary
          "
        >
          <div
            className="flex h-10 w-10 items-center justify-center rounded-sm
              border border-semantic-border-secondary
              bg-semantic-bg-secondary"
          >
            <GenericButton
              onClick={handleHamburgerClick}
              isIconOnly={true}
              icon={
                <HamburguerMenu className="h-4 w-4 text-semantic-text-primary" />
              }
              variant="refresh"
              size="md"
              customHeight="40px"
              className="rounded-full"
              aria-label="Toggle menu"
            />
          </div>
        </div>
      )}

      {/* Título principal con botón de regreso si es necesario */}
      <div className="flex flex-wrap items-center gap-3">
        {backRoute && (
          <GenericButton
            variant="secondary"
            shape="ghost"
            size="sm"
            icon={<LeftIcon className="h-5 w-5 text-semantic-text-secondary" />}
            onClick={() => navigate(backRoute)}
            aria-label="Go back"
          />
        )}
        <h2
          className={`
            text-xl
            font-medium
            text-[var(--semantic-text-secondary)]
            ${usesTitleSlot || headerActions ? "whitespace-nowrap" : ""}
          `}
        >
          {pageTitle}
        </h2>
        {/* Page-owned controls on the title's own line, only for routes that opted in — every
            other screen renders this row exactly as it did before the slot existed. */}
        {usesTitleSlot && <SideNavTitleSlotTarget className="min-w-0 flex-1" />}
        {/* CTX-112 — header actions the active page registered via
            PageHeaderActionsProvider (e.g. Insight Topology V2's compact
            toolbar), rendered beside the title exactly like the title slot. */}
        {headerActions && (
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3" data-testid="page-header-actions">
            {headerActions}
          </div>
        )}
      </div>

      <div className="w-3xl">
        <RenderTabsByRoute />
      </div>
    </header>
  );
}
